/**
 * Screen Flow Manager
 *
 * Manages verification screen navigation and state for standalone mode operation.
 * Provides a centralized state management system for screen transitions, navigation
 * history, and progress tracking throughout the verification flow.
 *
 * Key Features:
 * - Centralized screen state management
 * - Navigation history tracking with back navigation support
 * - Progress calculation based on verification flow stages
 * - Screen data persistence across navigation
 * - Session state restoration capabilities
 * - Event-driven architecture with listener pattern
 *
 * The manager follows a unidirectional data flow pattern where state changes
 * are managed centrally and propagated to registered listeners.
 *
 * @author SafePassage Engineering
 * @version 1.0.0
 */

import type {
  ScreenType,
  ScreenFlowState,
  ScreenFlowActions,
} from '../types/screens';
import type { ScreenDataMap } from '../types/screenData';
import { logger } from '../utils/logger';
import { sanitizeScreenDataForClient } from '../utils/verificationResultSanitizer';

const flowLogger = logger.clone({ labels: { component: 'ScreenFlowManager' }});

/**
 * Screen Flow Manager Class
 *
 * Core class for managing verification screen navigation and state.
 * Implements observer pattern for state change notifications and maintains
 * complete navigation history for back navigation support.
 *
 * The manager calculates progress based on verification flow stages and
 * provides methods for navigation, data persistence, and session recovery.
 */
export class ScreenFlowManager {
  private state: ScreenFlowState;
  private listeners: Array<(state: ScreenFlowState) => void> = [];
  private navigationCallback?: (from: ScreenType, to: ScreenType) => void;

  /**
   * Initialize Screen Flow Manager
   *
   * @param {ScreenType} [initialScreen='main'] - Starting screen for the flow
   */
  constructor(initialScreen: ScreenType = 'main') {
    this.state = {
      currentScreen: initialScreen,
      previousScreen: null,
      screenHistory: [initialScreen],
      canGoBack: false,
      progress: 0,
      screenData: {},
    };

    flowLogger.info('ScreenFlowManager initialized', { labels: { initialScreen: `${initialScreen}` } });
  }

  /**
   * Get current flow state
   *
   * Returns a copy of the current flow state to prevent external mutations.
   *
   * @returns {ScreenFlowState} Current flow state
   * @public
   */
  getState(): ScreenFlowState {
    return { ...this.state };
  }

  /**
   * Subscribe to state changes
   *
   * Registers a listener function that will be called whenever the flow state changes.
   * Returns an unsubscribe function for cleanup.
   *
   * @param {Function} listener - Callback function to receive state updates
   * @returns {Function} Unsubscribe function
   * @public
   */
  subscribe(listener: (state: ScreenFlowState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of state changes
   *
   * Calls all registered listener functions with the current state.
   *
   * @private
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.getState()));
  }

  /**
   * Get actions object
   *
   * Returns an object containing all available flow actions bound to this instance.
   *
   * @returns {ScreenFlowActions} Available flow actions
   * @public
   */
  getActions(): ScreenFlowActions {
    return {
      navigateToScreen: this.navigateToScreen.bind(this),
      goBack: this.goBack.bind(this),
      resetFlow: this.resetFlow.bind(this),
      updateScreenData: this.updateScreenData.bind(this),
    };
  }

  /**
   * Navigate to a new screen
   *
   * Handles screen transition with history tracking, progress calculation,
   * and optional data association. Prevents navigation to the same screen.
   *
   * @param {T} screen - Target screen identifier
   * @param {ScreenDataMap[T]} [data] - Optional screen-specific data
   * @private
   */
  private navigateToScreen<T extends ScreenType>(
    screen: T,
    data?: ScreenDataMap[T]
  ): void {
    const previousScreen = this.state.currentScreen;

    // Don't navigate if we're already on this screen
    if (previousScreen === screen) {
      flowLogger.debug('Already on screen, skipping navigation', { labels: { screen: `${screen}` } });
      return;
    }

    flowLogger.info('Navigating to screen', {
      labels: {
        from: `${previousScreen}`,
        to: `${screen}`,
      },
    });

    const screenHistory = [...this.state.screenHistory, screen];

    // Calculate progress based on screen
    const progress = this.calculateProgress(screen);

    this.state = {
      ...this.state,
      currentScreen: screen,
      previousScreen,
      screenHistory,
      canGoBack: screenHistory.length > 1,
      progress,
    };

    // Add screen data if provided
    if (data) {
      this.state.screenData[screen] = sanitizeScreenDataForClient(data);
    }

    // Call navigation callback if set
    if (this.navigationCallback) {
      this.navigationCallback(previousScreen, screen);
    }

    this.notifyListeners();
  }

  /**
   * Navigate to a screen from history (used by router)
   *
   * Public method for external navigation requests, typically from URL routing.
   *
   * @param {ScreenType} screen - Target screen identifier
   * @public
   */
  navigateToHistoryScreen(screen: ScreenType): void {
    this.navigateToScreen(screen);
  }

  /**
   * Set navigation callback
   *
   * Registers a callback function that will be called on every navigation.
   * Useful for external systems that need to track navigation events.
   *
   * @param {Function} callback - Callback function receiving from and to screens
   * @public
   */
  setNavigationCallback(
    callback: (from: ScreenType, to: ScreenType) => void
  ): void {
    this.navigationCallback = callback;
  }

  /**
   * Check if can navigate to screen (basic implementation)
   *
   * Currently allows navigation to any screen. Can be enhanced with
   * validation logic for flow restrictions.
   *
   * @returns {boolean} True if navigation is allowed
   * @public
   */
  canNavigateToScreen(): boolean {
    // For now, allow navigation to any screen
    // This can be enhanced with validation logic later
    return true;
  }

  /**
   * Go back to previous screen
   *
   * Navigates to the previous screen in the navigation history.
   * Updates progress and maintains navigation state.
   *
   * @private
   */
  private goBack(): void {
    if (!this.state.canGoBack || this.state.screenHistory.length <= 1) {
      flowLogger.warn('Cannot go back - no previous screen', {});
      return;
    }

    const currentScreen = this.state.currentScreen;
    const newHistory = [...this.state.screenHistory];
    newHistory.pop(); // Remove current screen
    const previousScreen = newHistory[newHistory.length - 1];

    flowLogger.info('Going back', {
      labels: {
        from: `${this.state.currentScreen}`,
        to: `${previousScreen}`,
      },
    });

    this.state = {
      ...this.state,
      currentScreen: previousScreen,
      previousScreen:
        this.state.screenHistory[this.state.screenHistory.length - 2] || null,
      screenHistory: newHistory,
      canGoBack: newHistory.length > 1,
      progress: this.calculateProgress(previousScreen),
    };

    if (this.navigationCallback) {
      this.navigationCallback(currentScreen, previousScreen);
    }
    this.notifyListeners();
  }

  /**
   * Reset flow to initial state
   *
   * Resets the flow manager to its initial state, clearing all history
   * and screen data. Useful for starting a new verification session.
   *
   * @private
   */
  private resetFlow(): void {
    flowLogger.info('Resetting flow', {});

    const previousScreen = this.state.currentScreen;
    this.state = {
      currentScreen: 'main',
      previousScreen: null,
      screenHistory: ['main'],
      canGoBack: false,
      progress: 0,
      screenData: {},
    };

    if (this.navigationCallback) {
      this.navigationCallback(previousScreen, 'main');
    }
    this.notifyListeners();
  }

  /**
   * Update screen data
   *
   * Updates data associated with a specific screen without changing
   * the current navigation state.
   *
   * @param {T} screen - Screen identifier
   * @param {ScreenDataMap[T]} data - Screen-specific data
   * @private
   */
  private updateScreenData<T extends ScreenType>(
    screen: T,
    data: ScreenDataMap[T]
  ): void {
    // Create a new screenData object with the updated data
    this.state.screenData = {
      ...this.state.screenData,
      [screen]: sanitizeScreenDataForClient(data),
    };
    this.notifyListeners();
  }

  /**
   * Calculate progress based on current screen
   *
   * Maps each screen to a progress percentage (0-100) representing
   * how far through the verification flow the user has progressed.
   *
   * @param {ScreenType} screen - Current screen identifier
   * @returns {number} Progress percentage (0-100)
   * @private
   */
  private calculateProgress(screen: ScreenType): number {
    const progressMap: Record<ScreenType, number> = {
      main: 0,
      'emblem-step-up': 5,
      'camera-access': 10,
      'camera-setup': 20,
      'desktop-waiting': 25,
      'id-request': 30,
      'id-capture': 50,
      'id-review': 70,
      'id-processing': 80,
      'selfie-retry': 60,
      loading: 90,
      'auto-return': 100,
      success: 100,
      failure: 0,
      recovery: 0,
      'return-to-desktop': 100,
    };

    return progressMap[screen] || 0;
  }

  /**
   * Restore state from session
   *
   * Restores the flow manager state from a previously saved session state.
   * Useful for recovering from page refreshes or navigation events.
   *
   * @param {Object} sessionState - Previously saved session state
   * @param {Partial<ScreenFlowState>} [sessionState.screenFlow] - Flow state data
   * @public
   */
  restoreState(sessionState: { screenFlow?: Partial<ScreenFlowState> }): void {
    if (sessionState && sessionState.screenFlow) {
      this.state = {
        ...this.state,
        ...sessionState.screenFlow,
      };

      flowLogger.info('State restored from session', {
        labels: {
          currentScreen: `${this.state.currentScreen}`,
        },
      });
      this.notifyListeners();
    }
  }
}
